import type { CSSProperties } from 'react';

import styles from './Spinner.module.css';

export interface SpinnerProps {
  /** Diameter in px. Defaults to the CSS default (1.25rem). */
  size?: number;
  /**
   * Accessible label. When provided the spinner announces itself as a live status;
   * otherwise it is decorative (`aria-hidden`) — use when a parent already conveys busy state.
   */
  label?: string;
  className?: string;
}

/**
 * Indeterminate loading ring. Track/indicator colors are component-local CSS vars
 * (`--spinner-track`, `--spinner-indicator`) so hosts like Button can recolor it.
 *
 * @interim Ported from the reference apps; replaceable by the UI Kit without API breakage.
 */
export function Spinner({ size, label, className }: SpinnerProps) {
  const style: CSSProperties = {};
  if (size !== undefined) (style as Record<string, string>)['--spinner-size'] = `${String(size)}px`;

  const classes = [styles.spinner, className].filter(Boolean).join(' ');
  const a11y = label ? { role: 'status', 'aria-label': label } : { 'aria-hidden': true };

  return <span className={classes} style={style} {...a11y} />;
}
